const DARK_LABEL_COLOR = '#1e293b';
const LIGHT_LABEL_COLOR = '#f8fafc';
const PROCESSED_ATTR = 'data-readable-label';

const SHAPE_SELECTOR = 'rect, polygon, circle, ellipse, path';
const SVG_TEXT_SELECTOR = 'text, tspan';
const HTML_LABEL_SELECTOR = 'foreignObject div, foreignObject span, foreignObject p';

type Rgb = { r: number; g: number; b: number };

function parseHexColor(value: string): Rgb | null {
  const hex = value.replace('#', '');
  const isShorthand = hex.length === 3;
  const isFull = hex.length === 6;

  // parseInt(_, 16) accepts trailing non-hex chars (e.g. "12zz" -> 18), so
  // validate the whole string before parsing to reject malformed fills.
  if ((!isShorthand && !isFull) || !/^[0-9a-f]+$/i.test(hex)) {
    return null;
  }

  const expanded = isShorthand
    ? hex
        .split('')
        .map((char) => char + char)
        .join('')
    : hex;
  const parsed = Number.parseInt(expanded, 16);

  if (Number.isNaN(parsed)) {
    return null;
  }

  return {
    r: (parsed >> 16) & 255,
    g: (parsed >> 8) & 255,
    b: parsed & 255,
  };
}

function parseRgbColor(value: string): Rgb | null {
  const match = value.match(/rgba?\(([^)]+)\)/i);

  if (!match?.[1]) {
    return null;
  }

  const channels = match[1].split(',').map((part) => Number.parseFloat(part.trim()));
  const [r, g, b] = channels;

  if (r === undefined || g === undefined || b === undefined) {
    return null;
  }

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return null;
  }

  return { r, g, b };
}

function parseColor(value: string | null): Rgb | null {
  if (!value) {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  const isUnusable =
    normalized === '' ||
    normalized === 'none' ||
    normalized === 'transparent' ||
    normalized.startsWith('url(');

  if (isUnusable) {
    return null;
  }

  if (normalized.startsWith('#')) {
    return parseHexColor(normalized);
  }

  return parseRgbColor(normalized);
}

function relativeLuminance({ r, g, b }: Rgb): number {
  const toLinear = (channel: number) => {
    const ratio = channel / 255;
    return ratio <= 0.03928 ? ratio / 12.92 : ((ratio + 0.055) / 1.055) ** 2.4;
  };

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

const DARK_LABEL_LUMINANCE = relativeLuminance({ r: 30, g: 41, b: 59 });
const LIGHT_LABEL_LUMINANCE = relativeLuminance({ r: 248, g: 250, b: 252 });

function contrastRatio(a: number, b: number): number {
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);

  return (lighter + 0.05) / (darker + 0.05);
}

function pickReadableLabelColor(fill: Rgb): string {
  const fillLuminance = relativeLuminance(fill);
  const darkContrast = contrastRatio(fillLuminance, DARK_LABEL_LUMINANCE);
  const lightContrast = contrastRatio(fillLuminance, LIGHT_LABEL_LUMINANCE);

  return darkContrast >= lightContrast ? DARK_LABEL_COLOR : LIGHT_LABEL_COLOR;
}

function readComputedFill(shape: Element): Rgb | null {
  const view = shape.ownerDocument?.defaultView;

  if (!view || typeof view.getComputedStyle !== 'function' || !shape.isConnected) {
    return null;
  }

  return parseColor(view.getComputedStyle(shape).fill);
}

function readShapeFill(node: Element): Rgb | null {
  const shapes = node.querySelectorAll(SHAPE_SELECTOR);

  for (const shape of shapes) {
    // Computed style resolves fills set via classDef CSS (including `!important`),
    // which inline style/attribute reads miss. Falls back to inline/attribute for
    // detached SVG (e.g. the string variant, where getComputedStyle is unavailable).
    const inlineFill = (shape as SVGElement).style?.fill;
    const fill =
      readComputedFill(shape) ??
      parseColor(inlineFill ?? null) ??
      parseColor(shape.getAttribute('fill'));

    if (fill) {
      return fill;
    }
  }

  return null;
}

function applyLabelColor(node: Element, color: string): void {
  node.querySelectorAll(SVG_TEXT_SELECTOR).forEach((element) => {
    element.setAttribute('fill', color);
    (element as SVGElement).style.fill = color;
  });

  node.querySelectorAll(HTML_LABEL_SELECTOR).forEach((element) => {
    (element as HTMLElement).style.color = color;
  });
}

/**
 * Sets each node's label color to contrast with its fill so author-styled
 * fills (e.g. a light green box) stay readable. Mermaid keeps label text at
 * the theme default color even when a node sets a custom fill, which makes
 * light fills unreadable in the dark theme.
 */
export function applyReadableNodeLabelColorsToElement(root: ParentNode): void {
  root.querySelectorAll('.node').forEach((node) => {
    if (node.hasAttribute(PROCESSED_ATTR)) {
      return;
    }

    const fill = readShapeFill(node);

    if (!fill) {
      return;
    }

    applyLabelColor(node, pickReadableLabelColor(fill));
    node.setAttribute(PROCESSED_ATTR, '');
  });
}

/**
 * String variant of {@link applyReadableNodeLabelColorsToElement} for renderers
 * that return SVG markup. Falls back to the original SVG if parsing fails or the
 * DOM APIs are unavailable (e.g. during SSR).
 */
export function applyReadableNodeLabelColors(svg: string): string {
  if (typeof DOMParser === 'undefined' || typeof XMLSerializer === 'undefined') {
    return svg;
  }

  try {
    const doc = new DOMParser().parseFromString(svg, 'image/svg+xml');

    if (doc.querySelector('parsererror')) {
      return svg;
    }

    applyReadableNodeLabelColorsToElement(doc);

    const root = doc.documentElement;

    return root ? new XMLSerializer().serializeToString(root) : svg;
  } catch {
    return svg;
  }
}
